import { QdsChoiceBase } from '../shared/choice-base.js';
import { createListboxOption, renderGroupedOptions } from '../shared/option-list.js';
import type { QdsOption } from '../../types/index';

/**
 * Inline known-option selection; navigation and selection remain independent.
 * Native invalid reporting focuses the visible control while preserving labels and cancelable invalid events.
 * @element qds-listbox
 * @attr {string} label - Visible listbox label and accessible name.
 * @attr {string} aria-label - Explicit accessible name.
 * @attr {string} aria-labelledby - External label IDs.
 * @attr {string} value - Default/reset scalar value; use the property for current state.
 * @attr {boolean} multiple - Select an array of values instead of one value.
 * @attr {string} name - Native submission field name.
 * @attr {string} form - Associated native form ID.
 * @attr {string} hint - Helper text.
 * @attr {string} error-text - Constraint or author error message.
 * @attr {boolean} disabled - Disable navigation/selection and omit submission.
 * @attr {boolean} readonly - Navigate without changing selection.
 * @attr {boolean} required - Require at least one available, enabled selection.
 * @attr {boolean} invalid - Author invalid state.
 * @attr {'sm'|'md'|'lg'} size - Option typography; targets remain at least 44px high.
 * @attr {'blur'|'input'|'submit'} validate-on - Error display timing, default blur.
 * @attr {string} empty-text - Visible empty list message, default No options available.
 * @property {ReadonlyArray<{value:string;label:string;disabled?:boolean;group?:string;description?:string}>} options - Plain-text options with immutable nonempty string identities.
 * @property {string} value - Current first selected value; silent owner assignment.
 * @property {string[]} values - Selected values in source-option order.
 * @property {string[]} defaultValues - Native reset defaults, overriding the value attribute.
 * @csspart label - Visible listbox label.
 * @csspart options - Named, scrollable listbox and sole Tab stop.
 * @csspart group - Named contiguous option group.
 * @csspart group-label - Visible group name.
 * @csspart option - Plain-text option; selected and active states are distinct.
 * @csspart empty - Empty-options status.
 * @csspart hint - Helper text.
 * @csspart error - Validation message.
 * @fires input - User selection; detail {component,value,values,multiple}.
 * @fires change - User selection; same detail as input.
 * @fires {{component:HTMLElement;valid:boolean;validity:{valid:boolean;valueMissing:boolean;customError:boolean};message:string}} qds-validity-change - Validity transition.
 */
export class QdsListbox extends QdsChoiceBase {
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'empty-text']; }
  private _activeValue = '';
  private _pointerValue = '';
  private _typed = '';
  private _typedAt = 0;
  private _empty?: HTMLParagraphElement;
  get emptyText(): string { return this.getAttribute('empty-text') || 'No options available.'; }
  set emptyText(value: string) { this.setAttribute('empty-text', value ?? ''); }
  override connectedCallback(): void {
    if (Object.prototype.hasOwnProperty.call(this, 'emptyText')) {
      const value = this.emptyText; Reflect.deleteProperty(this, 'emptyText'); this.emptyText = value;
    }
    super.connectedCallback();
  }
  protected override get _focusTarget(): HTMLElement | undefined { return this.disabled ? undefined : this._control; }
  protected get _choiceStyles(): string {
    return `:host { display:block; width:100%; }
      [part=options] { display:block; min-height:44px; max-height:var(--qds-listbox-max-height,18rem); overflow:auto;
      border:1px solid var(--theme-border-secondary,#a0aabe); border-radius:4px; background:var(--theme-background-primary,white); }
      [part=option] { display:flex; flex-direction:column; align-items:flex-start; justify-content:center;
        width:100%; min-width:44px; min-height:44px; padding:.625rem .75rem; border:0; border-radius:0;
        text-align:start; overflow-wrap:anywhere; color:var(--theme-text-primary,#333); background:transparent; font:inherit; cursor:pointer; }
      [part=option][aria-selected=true] { color:var(--theme-text-on-accent-primary,white); background:var(--theme-accent-primary,#445e89); }
      [part=options]:focus [data-active=true] { outline:2px solid var(--theme-text-primary,#333); outline-offset:-3px; }
      [part=options]:focus [aria-selected=true][data-active=true] { outline-color:var(--theme-text-on-accent-primary,white); }
      [part=option]:disabled { opacity:.55; cursor:not-allowed; }
      [part=group-label] { padding:.5rem .75rem; font-weight:600; background:var(--theme-background-secondary,#f9fafb); }
      .option-description { font-size:.875em; }
      [part=empty] { margin:.5rem 0; }
      :host([size=sm]) [part=option] { font-size:.8125rem; }
      :host([size=lg]) [part=option] { font-size:1rem; min-height:48px; }
      @media(forced-colors:active) { [part=option][aria-selected=true] { color:HighlightText; background:Highlight; }
        [part=options]:focus [data-active=true] { outline-color:CanvasText; }
        [part=options]:focus [aria-selected=true][data-active=true] { outline-color:HighlightText; } }`;
  }
  protected override _createOption(option: QdsOption): HTMLButtonElement {
    return createListboxOption(option);
  }
  protected override _renderOptions(): void {
    renderGroupedOptions(this._control!, this._options, option => this._createOption(option));
  }
  protected _syncChoices(): void {
    const control = this._control!; control.setAttribute('role', 'listbox'); control.tabIndex = this.disabled ? -1 : 0;
    for (const [name, value] of Object.entries({ 'aria-multiselectable': this.multiple, 'aria-disabled': this.disabled, 'aria-readonly': this.readonly, 'aria-required': this.required })) control.setAttribute(name, String(value));
    const enabled = this._options.filter(option => !option.disabled);
    if (this.disabled) this._activeValue = '';
    else if (!enabled.some(option => option.value === this._activeValue)) this._activeValue = this.values[0] || enabled[0]?.value || '';
    const selected = new Set(this.values);
    for (const [index, button] of this._buttons().entries()) {
      const option = this._options[index]; button.id = `${this._id}-option-${index}`; button.tabIndex = -1;
      button.disabled = this.disabled || Boolean(option.disabled); button.setAttribute('role', 'option'); button.setAttribute('aria-disabled', String(button.disabled));
      button.setAttribute('aria-selected', String(selected.has(option.value))); button.dataset.active = String(option.value === this._activeValue);
    }
    const active = this._buttons().find(button => button.dataset.choice === this._activeValue);
    if (active) control.setAttribute('aria-activedescendant', active.id); else control.removeAttribute('aria-activedescendant');
    if (!this._empty) { this._empty = document.createElement('p'); this._empty.setAttribute('part', 'empty'); this._empty.setAttribute('role', 'status'); control.after(this._empty); }
    this._empty.textContent = this._options.length ? '' : this.emptyText; this._empty.hidden = Boolean(this._options.length);
  }
  private _activate(value: string): void {
    this._activeValue = value; this._syncChoices();
    this._buttons().find(button => button.dataset.choice === value)?.scrollIntoView?.({ block: 'nearest' });
  }
  private _onFocus = (): void => {
    if (!this.disabled) this._activate(this._pointerValue || this.values[0] || this._options.find(option => !option.disabled)?.value || '');
  };
  private _onMouseDown = (event: MouseEvent): void => {
    if (event.target instanceof HTMLElement && event.target.closest('[data-choice]')) event.preventDefault();
  };
  private _onChoiceClick = (event: Event): void => {
    const button = event.target instanceof HTMLElement ? event.target.closest<HTMLButtonElement>('[data-choice]') : null;
    if (!button || button.disabled) return;
    this._pointerValue = button.dataset.choice!; this._control!.focus(); this._activate(this._pointerValue); this._pointerValue = '';
  };
  private _onKeyDown = (event: KeyboardEvent): void => {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || this.disabled) return;
    const enabled = this._options.filter(option => !option.disabled); if (!enabled.length) return;
    const current = Math.max(0, enabled.findIndex(option => option.value === this._activeValue)); let next: number | undefined;
    if (event.key === 'ArrowDown') next = Math.min(current + 1, enabled.length - 1);
    else if (event.key === 'ArrowUp') next = Math.max(current - 1, 0);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = enabled.length - 1;
    else if (event.key === 'PageDown') next = Math.min(current + 10, enabled.length - 1);
    else if (event.key === 'PageUp') next = Math.max(current - 10, 0);
    else if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); this._choose(this._activeValue); return; }
    else if ([...event.key].length === 1) {
      event.preventDefault();
      const now = Date.now(); this._typed = (now - this._typedAt > 700 ? '' : this._typed) + event.key.toLocaleLowerCase(); this._typedAt = now;
      const characters = [...this._typed], query = characters.every(character => character === characters[0]) ? characters[0] : this._typed;
      next = enabled.findIndex((_, index) => enabled[(current + 1 + index) % enabled.length].label.toLocaleLowerCase().startsWith(query));
      next = next < 0 ? undefined : (current + 1 + next) % enabled.length;
    }
    if (next !== undefined) { event.preventDefault(); this._activate(enabled[next].value); }
  };
  protected override _bind(): void {
    this._control!.addEventListener('focus', this._onFocus); this._control!.addEventListener('mousedown', this._onMouseDown);
    this._control!.addEventListener('click', this._onChoiceClick); this._control!.addEventListener('keydown', this._onKeyDown);
  }
  protected override _unbind(): void {
    this._control?.removeEventListener('focus', this._onFocus); this._control?.removeEventListener('mousedown', this._onMouseDown);
    this._control?.removeEventListener('click', this._onChoiceClick); this._control?.removeEventListener('keydown', this._onKeyDown);
    this._typed = ''; this._typedAt = 0; this._pointerValue = '';
  }
  override formResetCallback(): void {
    this._typed = ''; this._typedAt = 0; this._activeValue = ''; super.formResetCallback();
  }
}
if (!customElements.get('qds-listbox')) customElements.define('qds-listbox', QdsListbox);
